import { acceptedContactValues } from "../../../../../_shared/_logic/contact-comparison"
import { formatPropertyUnitLabel } from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import type {
  InboxWorkflowItem,
  InboxWorkflowRowOutcomeMap,
  InboxWorkflowState,
  InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import { formatInboxSourceLabel } from "../../../../../_shared/_logic/cells-presentation"
import {
  sourceCleanupInstruction,
  type SourceCleanupInstructionPresentation,
} from "../../../../../_shared/_logic/source-cleanup-instruction-tags"
import type { CustomerSourceCleanupRow } from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const stepOutput = (item: InboxWorkflowItem, stepKey: string): JsonObject =>
  objectValue(item.steps.find((entry) => entry.step_key === stepKey)?.output)

const relevantItem = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) =>
  item.action_case.flow_key === workflow.flow.flow_key &&
  (item.steps.some((entry) => entry.step_key === step.step_key) ||
    step.requirements_resolved_by_step.some((requirement) =>
      item.action_case.requirements.includes(requirement),
    ))

const itemNeedsStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.some(
    (requirement) =>
      item.action_case.requirements.includes(requirement) &&
      !item.action_case.resolved_requirements.includes(requirement),
  )

const itemResolvedStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = item.steps.find((entry) => entry.step_key === step.step_key)

  if (projected?.status === "resolved") return true

  if (projected?.status === "skipped") return false

  const requirements = step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const propertyPresentation = (payload: JsonObject) => {
  const property = objectValue(payload.property)
  const address = objectValue(property.address)
  const unit = objectValue(property.unit)
  const rawUnit = stringValue(
    unit.label,
    unit.hint,
    payload.property_unit_label,
    payload.unit_label,
    payload.unit_hint,
  )

  return {
    addressLabel:
      stringValue(
        address.label,
        address.freeform,
        property.label,
        payload.address_label,
        payload.property_label,
        payload.property_address,
        payload.address,
      ) || "Liegenschaft",
    unitLabel: rawUnit.toLocaleLowerCase("de-DE").startsWith("zuordnung:")
      ? "Gebäudeeinheit"
      : rawUnit
        ? formatPropertyUnitLabel(rawUnit)
        : "Gebäudeeinheit",
  }
}

const sourcePresentation = (payload: JsonObject, item: InboxWorkflowItem) => {
  const presentation = objectValue(payload.presentation)
  const origin = objectValue(payload.origin)

  return {
    sourceLabel: formatInboxSourceLabel(
      stringValue(
        presentation.source_label,
        payload.source_label,
        origin.source,
        item.action_case.source,
        item.action_case.channel,
      ),
    ),
    sourceSubLabel: stringValue(
      presentation.source_subline,
      presentation.source_sub_label,
      payload.source_subline,
    ),
  }
}

const contactPresentation = (payload: JsonObject, customerOutput: JsonObject) => {
  const requester = objectValue(payload.requester)
  const customer = objectValue(requester.customer)
  const suggested = objectValue(payload.suggested_contact)

  return {
    customerLabel:
      stringValue(
        customerOutput.display_name,
        customer.label,
        customer.name,
        requester.submitted_name,
        suggested.display_name,
        suggested.name,
        payload.customer_label,
        payload.customer_name,
      ) || "Ansprechpartner",
    customerEmail: acceptedContactValues(customerOutput.contact_points).email,
    customerPhone: acceptedContactValues(customerOutput.contact_points).phone,
  }
}

const contactKindLabel = (value: unknown) => {
  const kind = stringValue(value).toUpperCase()

  if (kind === "EMAIL") return "E-Mail"

  if (kind === "MOBILE") return "Mobil"

  if (kind === "FIXED_LINE") return "Festnetz"

  if (kind === "ADDRESS") return "Adresse"

  return "Kontaktweg"
}

const uniqueInstructions = (instructions: SourceCleanupInstructionPresentation[]) => {
  const seen = new Set<string>()

  return instructions.filter((instruction) => {
    const key = `${instruction.subLabel}|${instruction.label}|${instruction.copyValue}`

    if (seen.has(key)) return false

    seen.add(key)

    return true
  })
}

const contactPointOutputIsValid = (output: JsonObject): boolean => {
  if (output.decisions === undefined) return true

  if (!Array.isArray(output.decisions)) return false

  return output.decisions.every((rawDecision) => {
    const decision = objectValue(rawDecision)
    const action = stringValue(decision.action).toUpperCase()
    const kind = stringValue(decision.kind).toUpperCase()

    return (
      [
        "ACCEPT_CONTACT_POINT",
        "REPLACE_CONTACT_POINT",
        "REJECT_CONTACT_POINT",
        "DELETE_CONTACT_POINT",
      ].includes(action) && ["EMAIL", "MOBILE", "FIXED_LINE", "ADDRESS"].includes(kind)
    )
  })
}

const customerOutputIsValid = (output: JsonObject): boolean => {
  const decision = stringValue(output.decision)

  if (!["create_new", "use_existing", "ignored", "skip"].includes(decision)) return false

  if (output.contact_points === undefined) return true

  const contactPoints = objectValue(output.contact_points)

  return Object.keys(contactPoints).length > 0 && contactPointOutputIsValid(contactPoints)
}

const sourceInstructions = (
  payload: JsonObject,
  customerOutput: JsonObject,
  contactPointsOutput: JsonObject,
): SourceCleanupInstructionPresentation[] => {
  const instructions: SourceCleanupInstructionPresentation[] = []
  const contact = contactPresentation(payload, customerOutput)
  const customerDecision = stringValue(customerOutput.decision)

  if (customerDecision === "create_new") {
    instructions.push(
      sourceCleanupInstruction(
        contact.customerLabel,
        "Ansprechpartner neu anlegen",
        contact.customerLabel,
      ),
    )
  } else if (customerDecision === "use_existing") {
    instructions.push(
      sourceCleanupInstruction(
        contact.customerLabel,
        "Ansprechpartner-Zuordnung prüfen",
        contact.customerLabel,
      ),
    )
  } else if (customerDecision === "ignored" || customerDecision === "skip") {
    instructions.push(
      sourceCleanupInstruction(
        contact.customerLabel,
        "Ansprechpartner nicht übernehmen",
        contact.customerLabel,
      ),
    )
  }

  const decisions = Array.isArray(contactPointsOutput.decisions)
    ? contactPointsOutput.decisions
    : []
  for (const rawDecision of decisions) {
    const decision = objectValue(rawDecision)
    const action = stringValue(decision.action).toUpperCase()
    const kind = contactKindLabel(decision.kind)
    const value = stringValue(decision.value, decision.label) || kind

    if (action === "ACCEPT_CONTACT_POINT") {
      instructions.push(sourceCleanupInstruction(value, `${kind} ergänzen`, value))
    }

    if (action === "REPLACE_CONTACT_POINT") {
      instructions.push(sourceCleanupInstruction(value, `${kind} ersetzen`, value))
    }

    if (action === "REJECT_CONTACT_POINT") {
      instructions.push(sourceCleanupInstruction(value, `${kind} nicht übernehmen`, value))
    }

    if (action === "DELETE_CONTACT_POINT") {
      instructions.push(sourceCleanupInstruction(value, `${kind} löschen`, value))
    }
  }

  return uniqueInstructions(instructions)
}

const priorOutputError = "Gespeicherte Ansprechpartner-Entscheidung fehlt oder ist ungültig."
const cleanupOutputError = "Gespeicherter Abschluss des Quellenabgleichs fehlt oder ist ungültig."

export const buildCustomerSourceCleanupRows = ({
  workflow,
  step,
  rowOutcomes,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes: InboxWorkflowRowOutcomeMap
}): CustomerSourceCleanupRow[] =>
  workflow.items
    .filter((item) => relevantItem(item, workflow, step))
    .map((item) => {
      const actionCaseId = item.action_case.id
      const payload = objectValue(item.action_case.payload)
      const customerOutput = stepOutput(item, "verify_customer")
      const contactPointsOutput = objectValue(customerOutput.contact_points)
      const cleanupOutput = stepOutput(item, "source_cleanup")
      const needsStep = itemNeedsStep(item, step)
      const resolved = itemResolvedStep(item, step)
      const skipped =
        item.steps.find((entry) => entry.step_key === step.step_key)?.status === "skipped"
      const outcome = rowOutcomes[actionCaseId] ?? null
      const property = propertyPresentation(payload)
      const source = sourcePresentation(payload, item)
      const contact = contactPresentation(payload, customerOutput)
      const outputError = !customerOutputIsValid(customerOutput)
        ? priorOutputError
        : resolved && cleanupOutput.confirmed !== true
          ? cleanupOutputError
          : null
      const completed = cleanupOutput.confirmed === true
      const instructions = outputError
        ? []
        : sourceInstructions(payload, customerOutput, contactPointsOutput)
      const disabled =
        outcome?.status === "success" || !needsStep || !item.compatible || Boolean(outputError)

      return {
        id: actionCaseId,
        actionCaseId,
        workflowItem: item,
        step: item.steps.find((entry) => entry.step_key === step.step_key) ?? null,
        payload: item.action_case.payload,
        ...source,
        ...property,
        ...contact,
        sourceSyncInstructions: instructions,
        completed,
        needsStep,
        rowOutcome: outcome,
        isOpen: needsStep,
        isResolved: resolved,
        isCompatible: item.compatible,
        disabled,
        statusLabel:
          outcome?.status === "failed"
            ? "Prüfung nötig"
            : outputError
              ? "Datenfehler"
              : skipped
                ? "Übersprungen"
                : completed
                  ? "Erledigt"
                  : item.compatible
                    ? "Offen"
                    : "Nicht verfügbar",
        statusDetail: outcome?.error || outcome?.detail || outputError || item.error || null,
        searchText: [
          source.sourceLabel,
          property.addressLabel,
          property.unitLabel,
          contact.customerLabel,
          ...instructions.flatMap((instruction) => [
            instruction.label,
            instruction.subLabel,
            instruction.copyValue,
          ]),
        ].join(" "),
      }
    })
